export const MetricFormatSymbols = {
    percentage: '%',
    euro: '€',
    dollar: '$',
    number: '#',
    decimal: '0.00',
};
/**
 * Fixed metrics (always displayed)
 */
export const FIXED_METRICS = [
    {
        id: 'totalMediaCost',
        name: 'Total Media Cost',
        formula: '(campaignCost / clientSpent) * 100',
        format: 'percentage',
        dependencies: ['campaignCost', 'clientSpent'],
        isFixed: true,
        isPredefined: true,
    },
    {
        id: 'ctr',
        name: 'CTR',
        formula: '(campaignClicks / campaignImpressions) * 100',
        format: 'percentage',
        dependencies: ['campaignClicks', 'campaignImpressions'],
        isFixed: true,
        isPredefined: true,
    },
    {
        id: 'viewability',
        name: 'Viewability',
        formula: '(campaignViewableImpressions / campaignMeasurableImpressions) * 100',
        format: 'percentage',
        dependencies: ['campaignViewableImpressions', 'campaignMeasurableImpressions'],
        isFixed: true,
        isPredefined: true,
    },
];
/**
 * Optional pre-defined metrics
 */
export const OPTIONAL_METRICS = [
    {
        id: 'vtr',
        name: 'VTR',
        formula: '(campaignVideoEnd / campaignImpressions) * 100',
        format: 'percentage',
        dependencies: ['campaignVideoEnd', 'campaignImpressions'],
        isFixed: false,
        isPredefined: true,
    },
    {
        id: 'vcr',
        name: 'VCR',
        formula: '(campaignVideoEnd / campaignVideoStart) * 100',
        format: 'percentage',
        dependencies: ['campaignVideoEnd', 'campaignVideoStart'],
        isFixed: false,
        isPredefined: true,
    },
    {
        id: 'ecpm',
        name: 'eCPM',
        formula: '(campaignCost / campaignImpressions) * 1000',
        format: 'euro',
        dependencies: ['campaignCost', 'campaignImpressions'],
        isFixed: false,
        isPredefined: true,
    },
    {
        id: 'ecpc',
        name: 'eCPC',
        formula: 'campaignCost / campaignClicks',
        format: 'euro',
        dependencies: ['campaignCost', 'campaignClicks'],
        isFixed: false,
        isPredefined: true,
    },
    {
        id: 'ecpcv',
        name: 'eCPCV',
        formula: 'campaignCost / campaignVideoEnd',
        format: 'euro',
        dependencies: ['campaignCost', 'campaignVideoEnd'],
        isFixed: false,
        isPredefined: true,
    },
];
/**
 * Default direction for predefined metrics
 */
export const PREDEFINED_METRIC_DIRECTIONS = {
    totalMediaCost: 'close_to_target',
    ctr: 'higher_is_better',
    viewability: 'higher_is_better',
    vtr: 'higher_is_better',
    vcr: 'higher_is_better',
    ecpm: 'lower_is_better',
    ecpc: 'lower_is_better',
    ecpcv: 'lower_is_better',
};
/**
 * Default goal values for predefined metrics (editable by user)
 */
export const PREDEFINED_METRIC_DEFAULTS = {};
/**
 * Calculate default metric goals from KPI values (primaryKpi/secondaryKpi from Notion)
 *
 * Rules:
 * - CTR: higher_is_better, target = kpiValue (3 decimals), green >= target * 0.9, amber >= target * 0.8
 * - Viewability: higher_is_better, target = kpiValue (2 decimals), green >= target * 0.9, amber >= target * 0.8
 */
export function calculateDefaultGoalsFromKpis(primaryKpi, primaryKpiQuantification, secondaryKpi, secondaryKpiQuantification) {
    const goals = {};
    // Helper to process a KPI
    const processKpi = (kpiName, kpiValue) => {
        if (!kpiName || kpiValue == null)
            return;
        const normalizedName = kpiName.toLowerCase().trim();
        if (normalizedName === 'ctr') {
            const target = parseFloat(kpiValue.toFixed(3));
            goals.ctr = {
                target,
                direction: 'higher_is_better',
                greenBoundary: parseFloat((target * 0.9).toFixed(3)),
                amberBoundary: parseFloat((target * 0.8).toFixed(3)),
            };
        }
        else if (normalizedName === 'viewability') {
            const target = parseFloat(kpiValue.toFixed(2));
            goals.viewability = {
                target,
                direction: 'higher_is_better',
                greenBoundary: parseFloat((target * 0.9).toFixed(3)),
                amberBoundary: parseFloat((target * 0.8).toFixed(3)),
            };
        }
    };
    // Process both primary and secondary KPIs
    processKpi(primaryKpi, primaryKpiQuantification);
    processKpi(secondaryKpi, secondaryKpiQuantification);
    return goals;
}
/**
 * Build a metric goal for a KPI target given the metric's direction. The
 * amber/green boundaries are derived from the target so a KPI value alone is
 * enough to produce a usable RAG goal (all editable afterwards in the UI):
 *   higher_is_better → green ≥ 90% of target, amber ≥ 80%
 *   lower_is_better  → green ≤ 110% of target, amber ≤ 120%
 *   close_to_target  → green within 5% of target, amber within 10%
 */
function buildGoalForDirection(target, direction) {
    const round = (n) => parseFloat(n.toFixed(4));
    if (direction === 'lower_is_better') {
        return {
            target: round(target),
            direction,
            greenBoundary: round(target * 1.1),
            amberBoundary: round(target * 1.2),
        };
    }
    if (direction === 'close_to_target') {
        return {
            target: round(target),
            direction,
            greenBoundary: round(Math.abs(target) * 0.05),
            amberBoundary: round(Math.abs(target) * 0.1),
        };
    }
    return {
        target: round(target),
        direction,
        greenBoundary: round(target * 0.9),
        amberBoundary: round(target * 0.8),
    };
}
/**
 * Build default metric goals from the advia-os `kpis` object (e.g.
 * { "VTR": 75, "CTR": 0.5 }). Each KPI name is resolved against the known
 * metrics (by id or display name, case-insensitive) so any metric — fixed,
 * optional or custom — can carry a KPI target. Unrecognised KPI names are
 * skipped. The metric's direction comes from PREDEFINED_METRIC_DIRECTIONS,
 * defaulting to higher_is_better for custom metrics.
 */
export function buildGoalsFromKpis(kpis, metrics) {
    const goals = {};
    if (!kpis)
        return goals;
    // Resolve a KPI name to a metric id via id or display name (case-insensitive).
    const idByLabel = new Map();
    for (const m of metrics) {
        idByLabel.set(m.id.toLowerCase(), m.id);
        idByLabel.set(m.name.toLowerCase().trim(), m.id);
    }
    for (const [rawName, rawValue] of Object.entries(kpis)) {
        const value = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
        if (!Number.isFinite(value))
            continue;
        const metricId = idByLabel.get(rawName.toLowerCase().trim());
        if (!metricId)
            continue;
        const direction = PREDEFINED_METRIC_DIRECTIONS[metricId] ?? 'higher_is_better';
        goals[metricId] = buildGoalForDirection(value, direction);
    }
    return goals;
}
//# sourceMappingURL=metrics.js.map